<div class="modal-header media-picker-header">
  <h5 class="modal-title">{{ tr("textEditor.mediaModalTitle") }}</h5>
  <button
    type="button"
    class="btn-close"
    [attr.aria-label]="tr('common.cancel')"
    [disabled]="isProcessing()"
    (click)="cancel()"
  ></button>
</div>

<div
  class="modal-body media-picker-body"
  [attr.aria-busy]="
    isGeneratingAiImage() || isUploading() || isLoadingLibrary()
  "
>
  <div class="media-picker-tabs" role="tablist">
    <button
      type="button"
      class="media-picker-tab"
      role="tab"
      [class.active]="activeTab() === 'ai'"
      [attr.aria-selected]="activeTab() === 'ai'"
      [disabled]="isProcessing()"
      (click)="setActiveTab('ai')"
    >
      <svg class="sa-icon sa-bold sa-nofill" aria-hidden="true">
        <use href="/assets/icons/sprite.svg#zap"></use>
      </svg>
      {{ tr("textEditor.mediaTabAi") }}
    </button>
    <button
      type="button"
      data-testid="media-picker-tab-upload"
      class="media-picker-tab"
      role="tab"
      [class.active]="activeTab() === 'upload'"
      [attr.aria-selected]="activeTab() === 'upload'"
      [disabled]="isProcessing()"
      (click)="setActiveTab('upload')"
    >
      <svg class="sa-icon sa-bold sa-nofill" aria-hidden="true">
        <use href="/assets/icons/sprite.svg#upload-cloud"></use>
      </svg>
      {{ tr("textEditor.mediaTabUpload") }}
    </button>
    <button
      type="button"
      class="media-picker-tab"
      role="tab"
      [class.active]="activeTab() === 'library'"
      [attr.aria-selected]="activeTab() === 'library'"
      [disabled]="isProcessing()"
      (click)="setActiveTab('library')"
    >
      <svg class="sa-icon sa-bold sa-nofill" aria-hidden="true">
        <use href="/assets/icons/sprite.svg#collection"></use>
      </svg>
      {{ tr("textEditor.mediaTabLibrary") }}
    </button>
  </div>

  @if (errorMessage()) {
    <div class="alert alert-danger py-2 mt-3 mb-0">{{ errorMessage() }}</div>
  }

  @if (infoMessage()) {
    <div class="alert alert-info py-2 mt-3 mb-0">{{ infoMessage() }}</div>
  }

  @switch (activeTab()) {
    @case ("upload") {
      <section class="media-picker-panel">
        <label class="btn btn-primary w-100" [class.disabled]="isProcessing()">
          <input
            class="visually-hidden"
            type="file"
            data-testid="media-picker-upload-input"
            accept="image/png,image/jpeg,image/webp,image/gif"
            [disabled]="isProcessing()"
            (change)="uploadImage($event)"
          />
          @if (isUploading()) {
            {{ tr("textEditor.mediaGenerating") }}
          } @else {
            {{ tr("textEditor.mediaUpload") }}
          }
        </label>
        <p class="form-text mt-2 mb-0">PNG, JPG, WEBP ou GIF. Máximo 5 MB.</p>
      </section>
    }
    @case ("ai") {
      <section class="media-picker-panel" [formGroup]="aiForm">
        <label class="form-label" for="media-ai-prompt">
          {{ tr("textEditor.mediaPromptLabel") }}
        </label>
        <textarea
          id="media-ai-prompt"
          class="form-control"
          rows="4"
          maxlength="200"
          formControlName="prompt"
          [placeholder]="aiPromptPlaceholder()"
          [disabled]="isProcessing()"
        ></textarea>
        <div class="form-text">
          {{ tr("textEditor.mediaPromptHint") }}
        </div>

        <label class="form-label mt-3" for="media-ai-style">
          {{ tr("textEditor.mediaStyleLabel") }}
        </label>
        <select
          id="media-ai-style"
          class="form-select"
          formControlName="style"
          [disabled]="isProcessing()"
        >
          @for (style of aiImageStyleOptions; track style.value) {
            <option [value]="style.value">{{ tr(style.labelKey) }}</option>
          }
        </select>

        <button
          type="button"
          class="btn btn-primary w-100 mt-4"
          [class.media-picker-plan-disabled]="generatedImagesBlocked"
          [attr.aria-disabled]="generatedImagesBlocked"
          [disabled]="
            isProcessing() || (!generatedImagesBlocked && !canGenerateImage())
          "
          (click)="generateAiImage()"
        >
          @if (isGeneratingAiImage()) {
            {{ tr("textEditor.mediaGenerating") }}
          } @else {
            {{ tr("textEditor.mediaGenerateImage") }}
          }
        </button>
      </section>
    }

    @case ("library") {
      <section class="media-picker-panel">
        <div class="media-picker-library-toolbar">
          <div ngbDropdown class="dropdown">
            <button
              type="button"
              ngbDropdownToggle
              class="btn btn-sm btn-outline-secondary dropdown-toggle"
              aria-expanded="false"
              [disabled]="isProcessing()"
            >
              {{ libraryAssetFilterLabel() }}
            </button>
            <ul ngbDropdownMenu class="dropdown-menu dropdown-menu-end">
              <li>
                <button
                  type="button"
                  ngbDropdownItem
                  class="dropdown-item"
                  [class.active]="libraryAssetFilter() === 'all'"
                  [disabled]="isProcessing()"
                  (click)="setLibraryAssetFilter('all')"
                >
                  {{ tr("textEditor.mediaAssetTypeAll") }}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  ngbDropdownItem
                  class="dropdown-item"
                  [class.active]="
                    libraryAssetFilter() === workspaceAssetType.Image
                  "
                  [disabled]="isProcessing()"
                  (click)="setLibraryAssetFilter(workspaceAssetType.Image)"
                >
                  {{ tr("textEditor.mediaAssetTypeImage") }}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  ngbDropdownItem
                  class="dropdown-item"
                  [class.active]="
                    libraryAssetFilter() === workspaceAssetType.Audio
                  "
                  [disabled]="isProcessing()"
                  (click)="setLibraryAssetFilter(workspaceAssetType.Audio)"
                >
                  {{ tr("textEditor.mediaAssetTypeAudio") }}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  ngbDropdownItem
                  class="dropdown-item"
                  [class.active]="
                    libraryAssetFilter() === workspaceAssetType.Video
                  "
                  [disabled]="isProcessing()"
                  (click)="setLibraryAssetFilter(workspaceAssetType.Video)"
                >
                  {{ tr("textEditor.mediaAssetTypeVideo") }}
                </button>
              </li>
            </ul>
          </div>
        </div>

        @if (isLoadingLibrary()) {
          <app-processing-loader [compact]="true" />
        } @else if (filteredLibraryMediaAssets().length === 0) {
          <div class="media-picker-empty">
            {{ tr("textEditor.mediaLibraryEmpty") }}
          </div>
        } @else {
          <div class="media-picker-grid">
            @for (
              media of filteredLibraryMediaAssets();
              track media.workspaceAssetId
            ) {
              <button
                type="button"
                class="media-picker-tile"
                [attr.data-testid]="
                  'media-picker-library-item-' + media.workspaceAssetId
                "
                [class.selected]="
                  selectedLibraryAssetId() === media.workspaceAssetId
                "
                [attr.aria-pressed]="
                  selectedLibraryAssetId() === media.workspaceAssetId
                "
                [disabled]="isProcessing()"
                (click)="selectLibraryMedia(media.workspaceAssetId || 0)"
              >
                @if (isVideoMedia(media)) {
                  <app-vidstack-player
                    class="media-picker-tile-visual media-picker-tile-video"
                    [src]="media.url"
                    mediaType="video"
                    [title]="media.altText || media.fileName"
                    [muted]="true"
                    preload="metadata"
                    [interactive]="false"
                  ></app-vidstack-player>
                } @else if (isAudioMedia(media)) {
                  <div class="media-picker-tile-visual media-picker-tile-audio">
                    <svg class="sa-icon sa-bold sa-nofill" aria-hidden="true">
                      <use href="/assets/icons/sprite.svg#music"></use>
                    </svg>
                  </div>
                } @else {
                  <img
                    class="media-picker-tile-visual media-picker-tile-image"
                    [src]="media.url"
                    [alt]="media.altText || media.fileName"
                  />
                }

                <span class="media-picker-tile-name">{{ media.fileName }}</span>
                <span class="media-picker-check" aria-hidden="true"></span>
              </button>
            }
          </div>
        }
      </section>
    }
  }
</div>

<div class="modal-footer media-picker-footer">
  <button
    type="button"
    class="btn btn-light"
    [disabled]="isProcessing()"
    (click)="cancel()"
  >
    {{ tr("common.cancel") }}
  </button>
  <button
    type="button"
    class="btn btn-primary"
    data-testid="media-picker-confirm"
    [disabled]="isProcessing() || !canConfirm()"
    (click)="confirm()"
  >
    {{ tr("textEditor.mediaUseSelected") }}
  </button>
</div>

@if (isProcessing()) {
  <app-processing-loader appearance="overlay" />
}
